/* eslint-disable react/jsx-props-no-spreading */
import { Col, Row, Form, Select, Input, Button, Divider, Tag } from 'antd';
import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCatalogByName } from 'selectors/catalogos.selectors';
import PropTypes from 'prop-types';
import { validateFileList } from 'utils/form/validators';
import { BENEFICIADOS } from 'constants/nombresCatalogos';
import { groupObjectsByProperty } from 'utils/groupObjectsByProperty';
import { removeDuplicatesByProperty } from 'utils/removeDuplicatedByProperty';
import { Upload, Accordion, AddButton } from 'components';
import * as fieldMsgs from 'constants/formFieldsMessages';

const getBeneficiadosPorAsociacionId = (listaAsociaciones, beneficiadoId) => {
  const asociacion = listaAsociaciones?.find((b) => b.idTipoAsociacion === beneficiadoId);
  return asociacion?.beneficiarios || [];
};
const Comparecientes = ({ form, isLoadingImages }) => {
  const listaBeneficiadosCat = useSelector(selectCatalogByName(BENEFICIADOS));

  const getTiposAsociacionPorProcesoEspecifico = (
    listaBeneficiadosCompleta,
    procesoEspecificoId
  ) => {
    const listaBeneficiados = listaBeneficiadosCompleta.filter(
      (beneficiado) => beneficiado.idProcesoSIMAC === procesoEspecificoId
    );
    const asociaciones = groupObjectsByProperty(listaBeneficiados, 'tipoAsociacion');
    return Object.keys(asociaciones).map((tipoAsociacion) => {
      return {
        tipoAsociacion,
        idTipoAsociacion: asociaciones[tipoAsociacion][0].idTipoAsociacion,
        beneficiarios: asociaciones[tipoAsociacion]
      };
    });
  };

  const formComponent = ({ key, remove, name, disabled, isLastHallazgo, ...restField }) => (
    <div>
      <div className="formulario-hallazgos">
        <Row gutter={24}>
          <Col xs={10} className="formulario-hallazgos-tabla-header">
            Información del compareciente:
          </Col>
          <Col xs={3} className="formulario-hallazgos-tabla-header">
            Identificación anverso:
          </Col>
          <Col xs={3} className="formulario-hallazgos-tabla-header">
            Identificación reverso:
          </Col>
          <Col xs={3} className="formulario-hallazgos-tabla-header">
            Firma digital:
          </Col>
          <Col xs={2} style={{ textAlign: 'center' }} className="formulario-hallazgos-tabla-header">
            Acciones
          </Col>
        </Row>
        <Row>
          <Col xs={10} className="hallazgos-detalle">
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'nombre']}
                  label="Nombre(s):"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Nombre" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'apPaterno']}
                  label="Primer apellido:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Primer apellido" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item className="" name={[name, 'apMaterno']} label="Segundo apellido:">
                  <Input placeholder="Segundo apellido" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) => {
                    return prevValues.idProcesoEspecifico !== curValues.idProcesoEspecifico;
                  }}>
                  {() => {
                    const { idProcesoEspecifico } = form.getFieldsValue(['idProcesoEspecifico']);
                    const tiposAsociacionPorProcesoEspecifico =
                      getTiposAsociacionPorProcesoEspecifico(
                        listaBeneficiadosCat,
                        idProcesoEspecifico
                      );
                    return (
                      <Form.Item
                        className=""
                        name={[name, 'idTipoAsociacion']}
                        label="Tipo de asociación:"
                        rules={[{ required: true, message: fieldMsgs.ERR_SELECT_FIELD_REQUIRED }]}>
                        <Select
                          placeholder="Selecciona..."
                          fieldNames={{ value: 'idTipoAsociacion', label: 'tipoAsociacion' }}
                          options={tiposAsociacionPorProcesoEspecifico}
                          filterOption={(inputValue, option) =>
                            option.tipoAsociacion
                              ?.toUpperCase()
                              .indexOf(inputValue.toUpperCase()) !== -1
                          }
                          showSearch
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.comparecientes?.[key]?.idTipoAsociacion !==
                    curValues.comparecientes?.[key]?.idTipoAsociacion
                  }>
                  {() => {
                    const { idProcesoEspecifico } = form.getFieldsValue(['idProcesoEspecifico']);

                    const tiposAsociacionPorProcesoEspecifico =
                      getTiposAsociacionPorProcesoEspecifico(
                        listaBeneficiadosCat,
                        idProcesoEspecifico
                      );
                    const { comparecientes } = form.getFieldValue();

                    const beneficiadosPorAsociacion = getBeneficiadosPorAsociacionId(
                      tiposAsociacionPorProcesoEspecifico,
                      comparecientes?.[key]?.idTipoAsociacion
                    );
                    const sujetosObligados = removeDuplicatesByProperty(
                      beneficiadosPorAsociacion.map((beneficiado) => ({
                        sujetoObligado: beneficiado.sujetoObligado,
                        idSujetoObligado: beneficiado.idSujetoObligado
                      })),
                      'idSujetoObligado'
                    );
                    return (
                      <Form.Item
                        className=""
                        name={[name, 'idSujetoObligado']}
                        label="Sujeto obligado:"
                        rules={[{ required: true, message: fieldMsgs.ERR_SELECT_FIELD_REQUIRED }]}>
                        <Select
                          options={sujetosObligados}
                          placeholder="Selecciona"
                          fieldNames={{
                            label: 'sujetoObligado',
                            value: 'idSujetoObligado'
                          }}
                          filterOption={(inputValue, option) =>
                            option.sujetoObligado
                              ?.toUpperCase()
                              .indexOf(inputValue.toUpperCase()) !== -1
                          }
                          showSearch
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'puesto']}
                  label="Puesto:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Puesto" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'estadoCivil']}
                  label="Estado civil:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Estado civil" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'edad']}
                  label="Edad:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Edad" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  className=""
                  name={[name, 'domicilio']}
                  label="Domicilio:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Domicilio" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'ocupacion']}
                  label="Ocupación:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Escribe..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'identificacion']}
                  label="Identificación:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Escribe..." />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[7, 0]} className="row-formulario-nested">
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'folioIdentificacion']}
                  label="Folio de identificación:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Escribe..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className=""
                  name={[name, 'expedidaPor']}
                  label="Expedida por:"
                  rules={[{ required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED }]}>
                  <Input placeholder="Escribe..." />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col xs={3}>
            <div className="hallazgos-imagenes-row">
              <Form.Item
                {...restField}
                required
                name={[name, 'idAnverso']}
                className="hallazgos-imagenes-muestra"
                rules={[
                  {
                    validator: validateFileList
                  }
                ]}>
                <Upload disabled={isLoadingImages} loading={isLoadingImages} />
              </Form.Item>
            </div>
          </Col>
          <Col xs={3}>
            <div className="hallazgos-imagenes-row">
              <Form.Item
                {...restField}
                required
                name={[name, 'idReverso']}
                className="hallazgos-imagenes-muestra"
                rules={[
                  {
                    validator: validateFileList
                  }
                ]}>
                <Upload disabled={isLoadingImages} loading={isLoadingImages} />
              </Form.Item>
            </div>
          </Col>
          <Col xs={3}>
            <div className="hallazgos-imagenes-row">
              <Form.Item
                {...restField}
                required
                name={[name, 'firma']}
                className="hallazgos-imagenes-muestra"
                rules={[
                  {
                    validator: validateFileList
                  }
                ]}>
                <Upload disabled={isLoadingImages} loading={isLoadingImages} />
              </Form.Item>
            </div>
          </Col>
          <Col xs={2}>
            <div className="hallazgos-imagenes-acciones">
              <Button disabled={isLastHallazgo} style={{ height: '2em' }}>
                <DeleteOutlined onClick={() => remove(name)} />
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );

  return (
    <div className="modal-monitoreo-internet-hallazgos">
      <Row gutter={[24, 0]} className="row-formulario">
        <Col span={24} className="col-dos-instrucciones">
          <span className="instruction-span-purple" style={{ paddingLeft: '9px' }}>
            Agrega los registros necesarios.
          </span>
          <Tag
            className="instruction-span-pink"
            icon={<ExclamationCircleFilled style={{ fontSize: '10px' }} />}
            bordered={false}
            color="magenta"
            style={{ marginBottom: '6px', paddingLeft: '9px' }}>
            El registro de comparecientes es opcional. Si realizas el registro completa todos los
            campos solicitados.
          </Tag>
        </Col>
      </Row>
      <Form layout="vertical" form={form}>
        <Form.List name="comparecientes" initialValue={[{}]}>
          {(fields, { add, remove }) => {
            return (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const isLastHallazgo = fields.length <= 1;

                  const compareciente = {
                    key: `compareciente_${key}`,
                    label: `Compareciente ${name + 1}`,
                    forceRender: true,
                    children: formComponent({
                      key,
                      remove,
                      name,
                      isLastHallazgo,
                      ...restField
                    })
                  };
                  return (
                    <Accordion
                      key={key}
                      items={[compareciente]}
                      defaultActiveKey={['compareciente_0']}
                    />
                  );
                })}
                <Divider />
                <div className="formulario-hallazgos-acciones">
                  <AddButton
                    onClick={() => {
                      add();
                    }}
                    style={{ marginRight: '1em' }}>
                    Agregar Nuevo
                  </AddButton>
                </div>
              </>
            );
          }}
        </Form.List>
      </Form>
    </div>
  );
};

Comparecientes.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func,
    getFieldValue: PropTypes.func,
    getFieldInstance: PropTypes.func,
    getFieldsValue: PropTypes.func
  }).isRequired,
  isLoadingImages: PropTypes.bool.isRequired
};

export default Comparecientes;
